import { WithApiRequestMeta } from "../../api-results";
import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import { WorkspacesApiTag } from "./tags";
import { UserWorkspaceRole, Workspace, WorkspaceParticipant } from "./types";
import { EntitiesApiTag } from "../entities/tags";
import { SubscriptionTags } from "../subscription/tags";

export const addWorkspacesApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [
      WorkspacesApiTag.participants,
      WorkspacesApiTag.workspace,
      EntitiesApiTag.getEntityList,
      SubscriptionTags.subscriptionDetails,
    ],
  });

  const workspacesApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      createWorkspace: builder.mutation<
        void,
        WithApiRequestMeta<{
          data: {
            opts: { title: string };
            participants?: string[];
            display_pic_file?: File;
          };
          entityId: string;
        }>
      >({
        query: ({ data, entityId }) => {
          const formData = new FormData();
          formData.set("opts", JSON.stringify(data.opts));
          formData.set("participants", JSON.stringify(data.participants));
          if (data.display_pic_file) {
            formData.set("display_picture_file", data.display_pic_file);
          }
          return {
            url: `/api/auth/entity/${entityId}/workspaces`,
            data: formData,
            method: "POST",
          };
        },
      }),

      updateWorkspace: builder.mutation<
        Workspace,
        WithApiRequestMeta<{
          data: {
            opts?: {
              title?: string;
              admin_only_msg?: boolean;
              content_protected?: boolean;
            };
            participants?: string[];
            display_pic_file?: File;
          };
          entityId: string;
          id: string;
        }>
      >({
        query: ({ data, entityId, id }) => {
          const formData = new FormData();
          if (data.opts) {
            formData.set("opts", JSON.stringify(data.opts));
          }
          if (data.participants) {
            formData.set("participants", JSON.stringify(data.participants));
          }
          if (data.display_pic_file) {
            formData.set("display_picture_file", data.display_pic_file);
          }
          return {
            url: `/api/auth/entity/${entityId}/workspaces/${id}`,
            data: formData,
            method: "PUT",
          };
        },
        invalidatesTags: (a0, a1, { id }) => [
          { type: WorkspacesApiTag.workspace, id },
        ],
      }),

      getWorkspace: builder.query<
        Workspace,
        WithApiRequestMeta<{ workspaceId: string; entityId: string }>
      >({
        query: ({ workspaceId, entityId }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${workspaceId}`,
        }),
        providesTags: (a0, a1, { workspaceId }) => [
          { type: WorkspacesApiTag.workspace, id: workspaceId },
        ],
      }),
      getWorkspaceParticipants: builder.query<
        PaginatedResponse<WorkspaceParticipant>,
        WithApiRequestMeta<{
          entityId: string;
          workspaceId: string;
          params: PaginatedRequestParams<{
            search?: string;
            role?: "admin" | "member";
          }>;
        }>
      >({
        query: ({ entityId, workspaceId, params }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${workspaceId}/participants`,
          params,
        }),
        providesTags: (_result, _error, { workspaceId }) => [
          {
            type: WorkspacesApiTag.participants,
            id: `${workspaceId}`,
          },
        ],
      }),
      changeWorkspaceParticipantsRole: builder.mutation<
        void,
        WithApiRequestMeta<{
          users: string[];
          role: UserWorkspaceRole;
          entityId: string;
          workspaceId: string;
        }>
      >({
        query: ({ users, role, workspaceId, entityId }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${workspaceId}/participants`,
          method: "PUT",
          data: {
            users,
            role,
          },
        }),
        invalidatesTags: (e1, e2, { workspaceId }) => [
          {
            type: WorkspacesApiTag.participants,
            id: workspaceId,
          },
        ],
      }),
      addWorkspaceParticipants: builder.mutation<
        void,
        WithApiRequestMeta<{
          participants: string[];
          entityId: string;
          id: string;
        }>
      >({
        query: ({ participants, id, entityId }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${id}/add`,
          method: "POST",
          data: {
            participants,
          },
        }),
        invalidatesTags: (e1, e2, { id }) => [
          {
            type: WorkspacesApiTag.participants,
            id,
          },
          EntitiesApiTag.getEntityList,
          SubscriptionTags.subscriptionDetails,
        ],
      }),
      deleteWorkspaceParticipant: builder.mutation<
        void,
        WithApiRequestMeta<{
          id: string;
          userId: string;
          entityId: string;
        }>
      >({
        query: ({ id, userId, entityId }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${id}/${userId}`,
          method: "DELETE",
        }),
        invalidatesTags: (e1, e2, { id }) => [
          {
            type: WorkspacesApiTag.participants,
            id,
          },
          EntitiesApiTag.getEntityList,
          SubscriptionTags.subscriptionDetails,
        ],
      }),
      deleteWorkspace: builder.mutation<
        void,
        WithApiRequestMeta<{
          id: string;
          entityId: string;
        }>
      >({
        query: ({ id, entityId }) => ({
          url: `/api/auth/entity/${entityId}/workspaces/${id}`,
          method: "DELETE",
        }),
        // TODO: tags
      }),
    }),
    overrideExisting: true,
  });

  return workspacesApi;
};

export type WorkspacesApi = ReturnType<typeof addWorkspacesApi>;
